/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import {
  mergeProps,
  useFocusRing,
  useSlider,
  useSliderThumb,
  VisuallyHidden,
} from 'react-aria';
import { type SliderState, useSliderState } from 'react-stately';

import {
  type HtmlAttributes,
  type PolymorphicCommonProps,
} from '../_common/types';
import { Tooltip } from '../tooltip';

type SliderPropsBase = {
  /** Input props of the underlying input element */
  inputProps?: HtmlAttributes<'input'>;
  /** Determines the step size that will be added or removed from the current value when moving the slider button */
  step?: number;
  /** Whether the slider is disabled */
  isDisabled?: boolean;
  /** The minimum value possible to choose */
  minValue?: number;
  /** The maximum value possible to choose */
  maxValue?: number;
  /** Whether to display a tooltip with the current value when the slider is being dragged */
  showValues?: boolean;
  /** Whether to display visual indicators for each step in the slider */
  showSteps?: boolean;
};

type SingleSlider = SliderPropsBase & {
  /** Type of the slider */
  type?: 'single';
  /** Current value of the single slider. **Only required when the type is 'single'**. */
  value: number;
  /** Callback function triggered when the slider value(s) changes. The function parameter varies depending on the slider type, for single slider, it is a number and for range slider, it is an array of two numbers. */
  onChange: (value: number) => void;
};

type RangeSlider = SliderPropsBase & {
  /** Type of the slider */
  type: 'range';
  /** Current values of the range slider as an array of two numbers `[min, max]`. **Only required when the type is 'range'**.*/
  values: [number, number];
  /** Callback function triggered when the slider value(s) changes. The function parameter varies depending on the slider type, for single slider, it is a number and for range slider, it is an array of two numbers. */
  onChange: (value: [number, number]) => void;
};

/**
 * @remarks
 * The `type` prop is used to determine the type of the slider. It can be `single` or `range`. The `value` prop is required when the `type` is `single` and `values` is required when the `type` is `range`. It also affects the `onChange` prop type.
 */
type SliderProps = SingleSlider | RangeSlider;

export const Slider = <T extends React.ElementType = 'div'>({
  as,
  inputProps = {},
  className,
  style,
  minValue = 0,
  maxValue = 100,
  isDisabled = false,
  showSteps = false,
  showValues = false,
  step = 1,
  type = 'single',
  onChange,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, SliderProps>) => {
  const trackRef = React.useRef<HTMLDivElement | null>(null);

  const castedValue =
    type === 'range'
      ? (restProps as RangeSlider).values
      : (restProps as SingleSlider).value;

  const castedOnChange =
    type === 'range'
      ? (onChange as RangeSlider['onChange'])
      : (onChange as SingleSlider['onChange']);

  const state = useSliderState<typeof castedValue>({
    defaultValue: castedValue,
    isDisabled,
    maxValue,
    minValue,
    numberFormatter: new Intl.NumberFormat('en-US'),
    // oxlint-disable-next-line no-explicit-any
    onChange: castedOnChange as any,
    step,
  });

  const { setThumbValue } = state;

  const { groupProps, trackProps } = useSlider(
    { 'aria-label': 'slider' },
    state,
    trackRef,
  );

  const updatesSliderValue = useCallback(
    (newValue: number | [number, number]) => {
      if (type === 'single' && typeof newValue === 'number') {
        setThumbValue(0, newValue);
      } else if (type === 'range' && Array.isArray(newValue)) {
        setThumbValue(0, newValue[0]);
        setThumbValue(1, newValue[1]);
      }
    },
    [type, setThumbValue],
  );

  useEffect(() => {
    updatesSliderValue(castedValue);
    // Needed to make the slider work with controlled values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [castedValue]);

  const Component: React.ElementType = as ?? 'div';

  return (
    <Component
      className={classNames('ndl-slider', className)}
      style={style}
      role="presentation"
      ref={ref}
      {...groupProps}
      {...restProps}
      {...htmlAttributes}
    >
      {/* The track element holds the visible track line, the filled track line and the thumb. */}
      <div
        {...trackProps}
        ref={trackRef as React.RefObject<HTMLDivElement>}
        className={classNames('ndl-track', {
          'ndl-is-disabled': state.isDisabled,
        })}
      >
        {/* Circular marks, acting as indicators for the steps */}
        {showSteps && (
          <TrackMarks state={state} min={minValue} max={maxValue} />
        )}
        <FilledTrack state={state} />
        <Thumb
          index={0}
          state={state}
          trackRef={trackRef as React.RefObject<HTMLDivElement>}
          showValues={showValues}
          inputForwardedProps={inputProps}
        />
        {type === 'range' && (
          <Thumb
            index={1}
            state={state}
            trackRef={trackRef as React.RefObject<HTMLDivElement>}
            showValues={showValues}
            inputForwardedProps={inputProps}
          />
        )}
      </div>
    </Component>
  );
};

Slider.displayName = 'Slider';

function FilledTrack({ state }: { state: SliderState }) {
  let width: number;
  let left: number;
  if (state.values.length === 1) {
    width = state.getValuePercent(state.values[0]) * 100;
    left = 0;
  } else {
    width =
      state.getValuePercent(state.values[1]) * 100 -
      state.getValuePercent(state.values[0]) * 100;
    left = state.getValuePercent(state.values[0]) * 100;
  }

  return (
    <div
      className="ndl-filled-track"
      style={{
        left: `${left}%`,
        width: `${width}%`,
      }}
    />
  );
}

function TrackMarks({
  state,
  max,
  min,
}: {
  state: SliderState;
  max: number;
  min: number;
}) {
  const markNo = Math.round((max - min) / state.step);

  return (
    <div role="presentation" className="ndl-track-marks">
      {Array.from({ length: markNo - 1 }).map((_, index) => {
        const percent = (index / markNo) * 100;

        let isFilled;
        if (state.values.length === 1) {
          isFilled = state.getValuePercent(state.values[0]) * 100 > percent;
        } else {
          isFilled =
            state.getValuePercent(state.values[0]) * 100 < percent &&
            state.getValuePercent(state.values[1]) * 100 > percent;
        }

        return (
          <div
            key={`track-mark-${index}`}
            className={classNames('ndl-track-mark', {
              'ndl-on-active-track': isFilled,
            })}
            style={{
              left: `${((index + 1) / markNo) * 100}%`,
            }}
          />
        );
      })}
    </div>
  );
}

function Thumb(props: {
  index: number;
  state: SliderState;
  showValues?: boolean;
  trackRef: React.RefObject<HTMLDivElement>;
  inputForwardedProps: HtmlAttributes<'input'>;
}) {
  const { state, trackRef, index, inputForwardedProps } = props;
  const inputRef = React.useRef(null);
  const { thumbProps, inputProps, isDragging } = useSliderThumb(
    {
      index,
      inputRef,
      name: 'slider-thumb',
      trackRef,
    },
    state,
  );

  const { focusProps, isFocusVisible } = useFocusRing();

  /**
   * Solve issue with:
   * Warning: Received NaN for the `value` attribute. If this is expected, cast the value to a string.
   * Receiving NaN for other attributes also, like `max`. Waiting for a fix in react-aria.
   * @see https://github.com/adobe/react-spectrum/issues/5859
   * @see https://github.com/adobe/react-spectrum/pull/5861
   */
  const mergedProps = mergeProps(
    inputProps,
    focusProps,
    // Omitting aria-valuetext double announcements, unless explicitly provided
    { 'aria-valuetext': '' },
    inputForwardedProps,
  );
  const value =
    mergedProps.value !== undefined || mergedProps.value === 0
      ? mergedProps.value
      : '';

  return (
    <Tooltip
      isDisabled={props.showValues !== true}
      isOpen={isDragging || undefined}
      placement="top"
      type="simple"
    >
      <Tooltip.Trigger hasButtonWrapper>
        <div
          {...thumbProps}
          className={classNames('ndl-thumb', {
            'ndl-focus': isFocusVisible,
            'ndl-is-dragging': isDragging,
          })}
        >
          <VisuallyHidden>
            <input ref={inputRef} {...mergedProps} />
          </VisuallyHidden>
        </div>
      </Tooltip.Trigger>
      <Tooltip.Content>{value}</Tooltip.Content>
    </Tooltip>
  );
}
